import { useState, useEffect, useRef } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { formatDisplayDateTime } from '../../utils/format.js';
import {
  useUserWorkflowRuns,
  useTriggerWorkflow,
  useRetryDispatch,
} from '../../hooks/useWorkflows.js';
import { StatusBadge } from './StatusBadge.js';
import { WorkflowStepList } from './WorkflowStepList.js';
import { Button } from '../common/Button.js';
import { Spinner } from '../common/Spinner.js';

export function WorkflowHistoryPanel({
  userId,
  isArchived,
  autoExpand = false,
  embedded = false,
}: {
  userId: number;
  isArchived: boolean;
  autoExpand?: boolean;
  embedded?: boolean;
}) {
  const { data: runs, isLoading } = useUserWorkflowRuns(userId);
  const triggerMutation = useTriggerWorkflow(userId);
  const retryDispatchMutation = useRetryDispatch(userId);
  const [expandedRuns, setExpandedRuns] = useState<Set<number>>(new Set());
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoExpand && runs && runs.length > 0) {
      setExpandedRuns(new Set(runs.map((r) => r.id)));
      panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [autoExpand, runs]);

  const toggleExpand = (runId: number) => {
    setExpandedRuns((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });
  };

  // Disable trigger when any run is in-flight
  const hasInFlightRun = runs?.some((r) =>
    ['pending', 'dispatched', 'running'].includes(r.status)
  );

  return (
    <div ref={panelRef} className={embedded ? '' : 'bg-white rounded-lg border border-gray-200 p-6'}>
      <div className={`flex items-center ${embedded ? 'justify-end' : 'justify-between'} mb-6`}>
        {!embedded && <h3 className="text-lg font-semibold text-gray-900">Workflows</h3>}
        <Button
          variant="primary"
          size="sm"
          onClick={() => triggerMutation.mutate()}
          isLoading={triggerMutation.isPending}
          disabled={hasInFlightRun || triggerMutation.isPending}
          title={hasInFlightRun ? 'A workflow is already in progress' : undefined}
        >
          {isArchived ? 'Re-run offboarding workflow' : 'Run onboarding workflow now'}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : !runs || runs.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-sm text-gray-500">No workflow runs yet.</p>
          <p className="text-sm text-gray-400 mt-1">
            Workflow runs will appear here after this user is onboarded or offboarded.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {runs.map((run) => {
            const isExpanded = expandedRuns.has(run.id);
            return (
              <div key={run.id}>
                {/* Run row */}
                <button
                  onClick={() => toggleExpand(run.id)}
                  className="w-full flex items-center gap-3 py-3 min-h-[44px] text-left hover:bg-gray-50 rounded"
                  aria-expanded={isExpanded}
                  aria-controls={`steps-${run.id}`}
                >
                  <ChevronRightIcon
                    className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                  />
                  <span className="text-sm font-medium text-gray-700 capitalize">{run.type}</span>
                  <StatusBadge state={run.status} size="sm" />
                  <time
                    dateTime={run.dispatchedAt ?? run.createdAt}
                    className="text-xs text-gray-400"
                  >
                    {formatDisplayDateTime(run.dispatchedAt ?? run.createdAt)}
                  </time>
                  <span className="text-xs text-gray-400">
                    {run.completedAt
                      ? formatDisplayDateTime(run.completedAt)
                      : '\u2014'}
                  </span>
                </button>

                {/* Dispatch failed error + retry */}
                {run.status === 'dispatch_failed' && (
                  <div className="flex items-center gap-3 pl-7 pb-2 flex-wrap">
                    <span className="text-sm text-red-600">
                      Dispatch failed: {run.errorMessage || 'Unknown error'}. Click Retry dispatch
                      to try again.
                    </span>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        retryDispatchMutation.mutate(run.id);
                      }}
                      isLoading={
                        retryDispatchMutation.isPending && retryDispatchMutation.variables === run.id
                      }
                      disabled={retryDispatchMutation.isPending}
                    >
                      Retry dispatch
                    </Button>
                  </div>
                )}

                {/* Expanded step list */}
                {isExpanded && run.steps.length > 0 && (
                  <div id={`steps-${run.id}`}>
                    <WorkflowStepList steps={run.steps} userId={userId} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
